import React from 'react';
import { X, ExternalLink } from 'lucide-react';
import { designTokens } from '../styles/design-tokens';
import { useTheme } from '../context/ThemeContext';
import { Section, Field } from './education/Section';
import { ReviewEvidence } from './ReviewEvidence';
import { parseReview, landQualification, type ReviewEvidence as Evidence } from '../lib/review-evidence';

export interface LandListingProperties {
  review?: Evidence | string | null;
  source_checked_at?: string | null;
  id: string;
  listing_url: string | null;
  landwatch_url: string | null;
  listing_title: string | null;
  price: number | null;
  acreage: number | null;
  price_per_acre: number | null;
  address: string | null;
  county: string | null;
  state: string;
  seller_name: string | null;
}

interface LandListingDetailProps {
  listing: LandListingProperties;
  onClose: () => void;
}

export const LandListingDetail: React.FC<LandListingDetailProps> = ({ listing, onClose }) => {
  const { theme } = useTheme();
  const l = listing;
  const review = parseReview(l.review);

  return (
    <div style={{
      width: designTokens.layout.detailPanelWidth, background: theme.background,
      borderLeft: `1px solid ${theme.border}`,
      display: 'flex', flexDirection: 'column', overflow: 'hidden', flexShrink: 0,
    }}>
      {/* Header */}
      <div style={{
        padding: '16px', borderBottom: `1px solid ${theme.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
      }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ fontSize: '16px', fontWeight: 700, margin: 0, color: theme.text }}>
            {l.listing_title || 'Land Listing'}
          </h2>
          {l.price && (
            <div style={{ marginTop: '6px', fontSize: '18px', fontWeight: 500, color: designTokens.colors.warning }}>
              ${Number(l.price).toLocaleString()}
            </div>
          )}
        </div>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
          color: theme.textSecondary, flexShrink: 0,
        }}>
          <X size={18} />
        </button>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflow: 'auto', padding: '16px' }}>
        <Section title="Details">
          {l.acreage && <Field label="Advertised acreage" value={`${Number(l.acreage).toLocaleString()} acres`} />}
          {l.price_per_acre && <Field label="Price / advertised acre" value={`$${Number(l.price_per_acre).toLocaleString()}`} />}
          {review.stated_deeded_acres != null && <Field label="Seller-stated deeded" value={`${review.stated_deeded_acres.toLocaleString()} acres`} />}
          <Field label="Availability reported" value={review.availability || 'Not verified'} />
          <Field label="Status as of" value={review.status_as_of} />
          <Field label="Source checked" value={l.source_checked_at?.slice(0, 10)} />
          <Field label="Address" value={l.address} />
          <Field label="Location" value={[l.county, l.state].filter(Boolean).join(', ') || null} />
          <Field label="Listed by" value={l.seller_name} />
        </Section>

        <Section title="Large-acreage qualification">
          <div style={{ fontSize: 13, color: theme.text, lineHeight: 1.5 }}>{landQualification(review)}</div>
          <p style={{ fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>Listing acreage may include leased land. Seller-stated deeded acreage requires title and survey confirmation; it does not establish a contiguous, available development site.</p>
          {review.caveats?.map((caveat, i) => <p key={i} style={{ fontSize: 12, color: theme.textSecondary, lineHeight: 1.5 }}>{caveat}</p>)}
        </Section>
        <ReviewEvidence review={review} />

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginTop: '16px' }}>
          {l.listing_url && (
            <a
              href={l.listing_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '6px',
                background: designTokens.colors.warning, color: '#E4E6E8', fontSize: '13px',
                fontWeight: 500, textDecoration: 'none',
              }}
            >
              View listing <ExternalLink size={14} />
            </a>
          )}
          {l.landwatch_url && (
            <a
              href={l.landwatch_url}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px',
                padding: '8px 16px', borderRadius: '6px',
                background: designTokens.colors.success, color: '#E4E6E8', fontSize: '13px',
                fontWeight: 500, textDecoration: 'none',
              }}
            >
              LandWatch <ExternalLink size={14} />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};
